import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'icon';

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  loadingLabel?: string;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  fullWidth?: boolean;
};

/**
 * Variant → appearance map. Tokens-only. No raw colors.
 * Touch targets: sm=36px, md=44px (default), icon=44×44.
 *
 * Per DESIGN.md, only one `primary` instance should be visible per screen.
 */
const variantClass: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-neutral-0 hover:bg-primary-hover active:bg-primary-active border border-transparent',
  secondary:
    'bg-neutral-0 text-neutral-700 hover:bg-neutral-100 border border-neutral-200 active:bg-neutral-200',
  ghost:
    'bg-transparent text-neutral-600 hover:bg-neutral-100 border border-transparent active:bg-neutral-200',
  danger:
    'bg-danger text-neutral-0 hover:bg-danger-hover border border-transparent active:bg-danger-hover',
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    loadingLabel,
    leadingIcon,
    trailingIcon,
    fullWidth = false,
    disabled,
    className = '',
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const sizeClass =
    size === 'sm'
      ? 'h-9 px-3 text-sm font-medium gap-1.5'
      : size === 'icon'
        ? 'h-11 w-11 p-0 gap-0'
        : 'h-11 px-4 text-sm font-semibold gap-2';

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={
        'inline-flex items-center justify-center rounded-md transition-colors select-none ' +
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)] ' +
        'disabled:cursor-not-allowed disabled:border-transparent disabled:bg-neutral-300 disabled:text-neutral-500 ' +
        variantClass[variant] +
        ' ' +
        sizeClass +
        (fullWidth ? ' w-full' : '') +
        (className ? ` ${className}` : '')
      }
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          {loadingLabel && <span>{loadingLabel}</span>}
        </>
      ) : (
        <>
          {leadingIcon && <span className="inline-flex shrink-0">{leadingIcon}</span>}
          {size !== 'icon' && children}
          {size === 'icon' && (leadingIcon ?? children)}
          {trailingIcon && <span className="inline-flex shrink-0">{trailingIcon}</span>}
        </>
      )}
    </button>
  );
});
